import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setLocation } from "../config/redux/slices/LocationSlice";

// Utility function to get selected address from localStorage
export const getSelectedAddress = () => {
  try {
    const savedAddress = localStorage.getItem("selectedAddress");
    return savedAddress ? JSON.parse(savedAddress) : null;
  } catch (error) {
    console.error("Failed to get selected address from localStorage:", error);
    return null;
  }
};

// Returns city as selectedLocation + full location details managed by Redux
export const useGeolocation = (defaultLocation = "Gurugram") => {
  const dispatch = useDispatch();
  const locationState = useSelector((state) => state.location);

  const [selectedLocation, setSelectedLocation] = useState(defaultLocation); // ✅ city only
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [permissionDenied, setPermissionDenied] = useState(false); // ✅ new
  const [hasInitialized, setHasInitialized] = useState(false); // ✅ prevent re-runs

  useEffect(() => {
    // Only run once on mount, not on every selectedLocation change
    if (hasInitialized) return;

    // Check if there's already a location in Redux state
    if (locationState.city) {
      // Use existing city or construct address display from Redux state
      const displayLocation = locationState.addressLine1 || locationState.city;
      setSelectedLocation(displayLocation);
      setHasInitialized(true);

      return;
    }

    // Check if there's already a selected address in localStorage
    const existingAddress = getSelectedAddress();
    if (existingAddress) {
      // Use addressLine1 if available, otherwise fall back to city
      const displayLocation =
        existingAddress.addressLine1 || existingAddress.city || defaultLocation;
      setSelectedLocation(displayLocation);
      dispatch(setLocation(existingAddress));
      setHasInitialized(true);

      return;
    }

    let cancelled = false;
    const getPosition = () =>
      new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation not supported"));
          return;
        }
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000, // 5 minutes cache
        });
      });

    const fetchReverseGeocode = async (latitude, longitude) => {
      const res = await fetch(`/api/geocode?lat=${latitude}&lng=${longitude}`);
      return res.json();
    };

    const request = async () => {
      setIsFetchingLocation(true);
      try {
        // Try Permissions API to detect denial without throwing UI prompts
        if (navigator.permissions && navigator.permissions.query) {
          try {
            const status = await navigator.permissions.query({
              name: "geolocation",
            });
            if (status.state === "denied") {
              if (!cancelled) {
                setPermissionDenied(true);
                setSelectedLocation(defaultLocation);
                // Set default location in Redux when permission denied
                dispatch(
                  setLocation({
                    city: defaultLocation,
                    latitude: null,
                    longitude: null,
                    addressLine1: "",
                    addressLine2: "",
                    state: "",
                    country: "",
                    postalCode: "",
                  })
                );
                setHasInitialized(true);
              }
              return;
            }
          } catch {}
        }

        const pos = await getPosition();
        const { latitude, longitude } = pos.coords;
        const data = await fetchReverseGeocode(latitude, longitude);

        if (!cancelled) {
          if (data.status === "OK" && data.results.length > 0) {
            const result = data.results[0];
            const components = result.address_components;
            const getComponent = (type) =>
              components.find((c) => c.types.includes(type))?.long_name || "";

            const city =
              getComponent("locality") ||
              getComponent("administrative_area_level_2") ||
              "";
            const state = getComponent("administrative_area_level_1");
            const country = getComponent("country");
            const postalCode = getComponent("postal_code");

            const addressLine1 = result.formatted_address.split(",")[0] || "";
            const addressLine2 = result.formatted_address
              .split(",")
              .slice(1)
              .join(", ")
              .trim();

            const locationData = {
              city: city || defaultLocation,
              latitude,
              longitude,
              addressLine1,
              addressLine2,
              state,
              country,
              postalCode,
            };

            setSelectedLocation(city || defaultLocation);

            // Dispatch to Redux store
            dispatch(setLocation(locationData));

            // Save detected location as selected address in localStorage
            try {
              localStorage.setItem(
                "selectedAddress",
                JSON.stringify(locationData)
              );
            } catch (error) {
              console.error(
                "Failed to save auto-detected location to localStorage:",
                error
              );
            }

            setPermissionDenied(false);
          } else {
            setSelectedLocation(defaultLocation);
          }
          setHasInitialized(true);
        }
      } catch (err) {
        // Suppress noisy console; set graceful fallback instead
        if (!cancelled) {
          if (err?.code === 1 /* PERMISSION_DENIED */) {
            setPermissionDenied(true);
            // Clear Redux location state on permission denied
            dispatch(
              setLocation({
                city: defaultLocation,
                latitude: null,
                longitude: null,
                addressLine1: "",
                addressLine2: "",
                state: "",
                country: "",
                postalCode: "",
              })
            );
            setSelectedLocation(defaultLocation);
          } else {
            setSelectedLocation(defaultLocation);
          }
          setHasInitialized(true);
        }
      } finally {
        if (!cancelled) setIsFetchingLocation(false);
      }
    };

    request();
    return () => {
      cancelled = true;
    };
  }, [defaultLocation, hasInitialized]); // ✅ removed selectedLocation dependency

  // Listen for Redux location state changes and update local selectedLocation
  useEffect(() => {
    if (hasInitialized && locationState.city) {
      const displayLocation = locationState.addressLine1 || locationState.city;
      setSelectedLocation(displayLocation);
    }
  }, [locationState.addressLine1, locationState.city, hasInitialized]);

  // Function to manually update the selected location (called from components)
  const updateSelectedLocation = useCallback(
    (newLocation) => {
      setSelectedLocation(newLocation);
      // If newLocation is a string, treat it as city
      if (typeof newLocation === "string") {
        dispatch(
          setLocation({
            ...locationState,
            city: newLocation,
          })
        );
      } else {
        // If it's an object, update the full location
        dispatch(setLocation(newLocation));
      }
    },
    [dispatch, locationState]
  );

  // ✅ returns both city and Redux location state + permissionDenied
  return {
    selectedLocation,
    setSelectedLocation: updateSelectedLocation, // Use the wrapped function
    locationDetails: locationState, // Use Redux state instead of local state
    isFetchingLocation,
    permissionDenied, // 👈 use this in your UI
  };
};
